// Select.tsx
import React, { useState, useCallback } from "react"
import { StyleSheet } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { CenterFlex, ColumnFlex, Flex, IconFont, StartFlex } from "~components/El"

export type SelectOption = {
  label: string
  value: string
  /** 选项下方次要说明（如时段起止释义） */
  subLabel?: string
}

export interface SelectProps {
  options: SelectOption[]
  value?: string
  defaultValue?: string
  LabelComponent?: any
  title?: string
  titleIcon?: string
  description?: string
  onChange?: (value: string, option: SelectOption) => void
}

const Container = styled(Flex)`
  flex-direction: column;
  height: auto;
  width: 100%;
`
const Description = styled(Flex)`
  flex-direction: column;
  padding: 12px 0;
`
const Title = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  padding: 3px 0px;
  margin-left: ${p=>p.ml||0};
`
const Desc = styled.Text`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  padding: 2px 0px;
`

const Item = styled(Flex)<{ active: boolean }>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  border-top-width: ${StyleSheet.hairlineWidth}px;
  border-top-color: ${({ theme }) => theme.colors.border};
  border-top-style: solid;
`
const Label = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`
const OptionSubLabel = styled.Text`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
`

const Select: React.FC<SelectProps> = ({
  options,
  value,
  defaultValue,
  LabelComponent,
  title = "",
  titleIcon = "",
  description = "",
  onChange,
}) => {
  const theme = useTheme()

  const [innerValue, setInnerValue] = useState(defaultValue)
  const currentValue = value ?? innerValue

  const handlePress = useCallback(
    (option: SelectOption) => {
      setInnerValue(option.value)
      onChange?.(option.value, option)
    },
    [onChange],
  )

  return (
    <Container>
      <Description>
        {title ? (
          <StartFlex>
            {titleIcon ? (
              <IconFont name={titleIcon} size={16} color={theme.colors.primary} />
            ) : null}
            <Title ml={titleIcon ? 8 : 0}>{title}</Title>
          </StartFlex>
        ) : null}
        {description ? <Desc>{description}</Desc> : null}
      </Description>

      {options.map((option) => {
        const active = option.value === currentValue
        return (
          <Item key={option.value} active={active} onClick={() => handlePress(option)}>
            {LabelComponent ? (
              <LabelComponent active={active} item={option} />
            ) : (
              <ColumnFlex flex={9} alignItems="flex-start">
                <Label>{option.label}</Label>
                {option.subLabel ? <OptionSubLabel>{option.subLabel}</OptionSubLabel> : null}
              </ColumnFlex>
            )}
            <CenterFlex flex={1}>
              <IconFont
                name={active ? "cg" : "yuan"}
                size={16}
                color={active ? theme.colors.primary : theme.colors.border}
              />
            </CenterFlex>
          </Item>
        )
      })}
    </Container>
  )
}

export default Select
